import { useEffect, useRef, useState } from 'react';

import { almacenToken } from '../lib/api';
import { baseApi } from '../lib/servidor';

/**
 * Lo que se ve en la reja, en vivo.
 *
 * Es la cámara que retrata al visitante, y el guardia puede mirarla **cuando
 * quiera**: no depende de que haya una visita a medio registrar. Una cámara en
 * la entrada sirve justo para las veces en que todavía no hay nada registrado
 * —alguien esperando afuera, un coche parado, un ruido— y atarla a un trámite
 * en curso la volvería inútil en esos momentos.
 *
 * ## Por qué cuadros sueltos y no video
 *
 * La cámara entrega un JPEG a la carta en ~0,35 s, así que se le piden ~2 por
 * segundo y ya. La alternativa —abrir el flujo de video— obligaría a
 * decodificar H.265 en el servidor y volver a codificarlo para el navegador,
 * porque ningún navegador reproduce H.265 con fiabilidad. A dos cuadros por
 * segundo el guardia ve perfectamente quién está en la reja, que es la
 * pregunta que hace.
 */

/**
 * Cada cuánto se pide un cuadro.
 *
 * La cámara tarda ~0,35 s en entregarlo, así que pedir más seguido solo
 * encolaría peticiones. Es la misma cadencia a la que ya trabaja la charola.
 */
const CADENCIA_MS = 500;

/** Tras cuántos fallos seguidos se dice que no hay imagen. */
const FALLOS_PARA_AVISAR = 3;

export function CamaraDeReja({ camaraId, titulo }: { camaraId: string; titulo: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [caida, setCaida] = useState(false);
  const anterior = useRef<string | null>(null);

  useEffect(() => {
    let vivo = true;
    let reloj: ReturnType<typeof setTimeout> | null = null;
    let fallos = 0;

    async function traer(): Promise<void> {
      try {
        const r = await fetch(`${baseApi()}/api/camaras/${camaraId}/vista`, {
          headers: { Authorization: `Bearer ${almacenToken.leer() ?? ''}` },
          cache: 'no-store',
        });
        if (!vivo) return;

        if (r.ok) {
          const u = URL.createObjectURL(await r.blob());
          if (!vivo) {
            URL.revokeObjectURL(u);
            return;
          }
          // Cada blob se devuelve a mano. A dos por segundo, no hacerlo son
          // siete mil imágenes retenidas en una hora de turno.
          if (anterior.current) URL.revokeObjectURL(anterior.current);
          anterior.current = u;
          setUrl(u);
          fallos = 0;
          setCaida(false);
        } else {
          fallos += 1;
          if (fallos >= FALLOS_PARA_AVISAR) setCaida(true);
        }
      } catch {
        fallos += 1;
        if (fallos >= FALLOS_PARA_AVISAR) setCaida(true);
      } finally {
        // Se reprograma al **terminar**, no con un intervalo fijo: si la red va
        // lenta, un intervalo encolaría peticiones que se pisan y el retraso
        // crecería sin parar hasta que la imagen fuera de hace un minuto.
        if (vivo) reloj = setTimeout(() => void traer(), CADENCIA_MS);
      }
    }

    void traer();

    return () => {
      vivo = false;
      if (reloj) clearTimeout(reloj);
      if (anterior.current) URL.revokeObjectURL(anterior.current);
      anterior.current = null;
    };
  }, [camaraId]);

  return (
    <figure className="relative overflow-hidden rounded-xl border border-outline-variant bg-black">
      {url ? (
        <img src={url} alt={`Vista en vivo de ${titulo}`} className="block w-full" />
      ) : (
        <div className="flex aspect-video items-center justify-center text-body-md text-on-surface-variant">
          Conectando con la cámara…
        </div>
      )}

      {/* **Se conserva el último cuadro y se avisa encima.** Poner la pantalla
          en negro al primer fallo haría parpadear la imagen con cualquier
          tropiezo de red; pero dejar un cuadro viejo sin decirlo sería peor —el
          guardia creería estar viendo la reja de ahora—. */}
      {caida && (
        <figcaption className="absolute inset-x-0 bottom-0 bg-error px-3 py-1.5 text-label-sm text-on-error">
          Sin imagen — lo que se ve es el último cuadro que llegó
        </figcaption>
      )}
    </figure>
  );
}
